import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthJwtService } from '../../services/auth-jwt.service';
import { DynamicCssService } from '../../services/dynamic-css.service';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

interface MenuItem {
  label: string;
  route: string;
  iconClass: string;
  roles?: string[];
}

@Component({
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule
  ],
  selector: 'app-default-layout',
  standalone: true,
  templateUrl: './default-layout.component.html',
  styles: [
    `
    body {
      transition: opacity 0.3s ease-in-out;
    }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  pageTitle = 'Whatsapp Business';
  isSidebarCollapsed = false;
  private routerSubscription?: Subscription;
  private readonly collapseBreakpoint = 767.98;

  private readonly pageTitles = [
    { prefix: '/home', title: 'Inicio' },
    { prefix: '/admin/account', title: 'Cuentas' },
    { prefix: '/admin/client', title: 'Clientes' },
    { prefix: '/admin/project', title: 'Proyectos' },
    { prefix: '/admin/template', title: 'Plantillas' },
    { prefix: '/admin', title: 'Administración' },
    { prefix: '/whatsapp/conversations', title: 'Conversaciones' },
    { prefix: '/whatsapp/send-message', title: 'Enviar mensaje' },
    { prefix: '/whatsapp/orders', title: 'Envíos' },
  ];

  private readonly allMenuItems: MenuItem[] = [
    { label: 'Inicio', route: '/home', iconClass: 'bi bi-house' },
    { label: 'Conversaciones', route: '/whatsapp/conversations', iconClass: 'bi bi-chat-dots' },
    { label: 'Enviar mensaje', route: '/whatsapp/send-message', iconClass: 'bi bi-whatsapp' },
    { label: 'Envíos', route: '/whatsapp/orders', iconClass: 'bi bi-send-check' },
    { label: 'Cuentas', route: '/admin/account', iconClass: 'bi bi-building', roles: ['Administrador'] },
    { label: 'Clientes', route: '/admin/client', iconClass: 'bi bi-people', roles: ['Administrador'] },
    { label: 'Plantillas', route: '/admin/template', iconClass: 'bi bi-file-earmark-text', roles: ['Administrador'] }
  ];

  menuItems: MenuItem[] = [];

  constructor(
    private router: Router,
    private authJwtService: AuthJwtService,
    private dynamicCss: DynamicCssService
  ) { }

  ngOnInit(): void {
    const token = this.authJwtService.getToken();
    const userData = this.authJwtService.getUserDataManual(token);
    const userRoles: string[] = userData?.RolesListString ?? [];
    this.menuItems = this.allMenuItems.filter(item =>
      !item.roles || item.roles.some(r => userRoles.includes(r))
    );

    this.isSidebarCollapsed = window.innerWidth <= this.collapseBreakpoint;
    document.body.style.opacity = '0';
    this.dynamicCss.loadStylesheetLink('admin.css', 'admin-css').then(() => {
      document.body.style.opacity = '1';
    });
    this.dynamicCss.loadStylesheetLink(
      'https://cdnjs.cloudflare.com/ajax/libs/bootstrap-icons/1.11.0/font/bootstrap-icons.min.css',
      'bootstrap-icons'
    );
    this.updatePageTitle(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        this.updatePageTitle(event.urlAfterRedirects);
      });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  private updatePageTitle(url: string): void {
    const match = this.pageTitles.find(item => url.startsWith(item.prefix));
    this.pageTitle = match ? match.title : 'Whatsapp Business';
    document.title = `Whatsapp Business - ${this.pageTitle}`;
  }

  logout(): void {
    this.authJwtService.logout();
    this.router.navigate(['/login']);
  }
}
